import React from 'react';

/**
 * PageHero — Reusable dark hero banner for all inner pages.
 * Consistent with the StreamFlow reference design.
 * 
 * Props:
 *  - label: small uppercase accent text above title (e.g. "EXPLORE")
 *  - title: main bold heading (string or JSX)
 *  - subtitle: paragraph below title
 *  - children: optional CTA buttons or extra content below subtitle
 *  - align: 'center' | 'left' (default: 'center')
 *  - blobColors: override default blob tints
 */
const PageHero = ({
  label,
  title,
  subtitle,
  children,
  align = 'center',
  className = '',
}) => {
  const textAlign = align === 'left' ? 'text-left' : 'text-center';
  const itemsAlign = align === 'left' ? 'items-start' : 'items-center';

  return (
    <div className={`relative overflow-hidden pt-28 pb-12 md:pt-36 md:pb-16 ${className}`}>
      {/* Decorative blobs */}
      <div
        className="pointer-events-none absolute inset-0 overflow-hidden"
        aria-hidden="true"
        style={{ WebkitMaskImage: 'linear-gradient(to bottom, black 60%, transparent 100%)', maskImage: 'linear-gradient(to bottom, black 60%, transparent 100%)' }}
      >
        <div className="blob bg-purple-600/20 w-72 h-72 -top-10 -left-16 mix-blend-screen" />
        <div className="blob bg-cyan-500/15 w-80 h-80 top-20 right-0 mix-blend-screen" />
        <div className="blob bg-indigo-500/10 w-48 h-48 bottom-0 left-1/3 mix-blend-screen" />

        {/* Floating decorative music notes — match reference design */}
        <svg className="absolute top-16 left-[8%] opacity-20 float-shape" style={{ animationDelay: '0s' }} width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#8B5CF6" strokeWidth="1.5"><path d="M9 18V5l12-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/></svg>
        <svg className="absolute top-32 right-[12%] opacity-15 float-shape" style={{ animationDelay: '0.8s' }} width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#22D3EE" strokeWidth="1.5"><path d="M9 18V5l12-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/></svg>
        <svg className="absolute bottom-8 left-[18%] opacity-20 float-shape" style={{ animationDelay: '1.5s' }} width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#FBBF24" strokeWidth="1.5"><path d="M9 18V5l12-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/></svg>
        <svg className="absolute top-20 left-[40%] opacity-10 float-shape" style={{ animationDelay: '2.2s' }} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#22C55E" strokeWidth="1.5"><path d="M9 18V5l12-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/></svg>
        {/* Stars / sparkles */}
        <div className="absolute top-24 right-[30%] w-1.5 h-1.5 rounded-full bg-white/30 float-shape" style={{ animationDelay: '0.4s' }} />
        <div className="absolute top-48 left-[25%] w-1 h-1 rounded-full bg-purple-400/40 float-shape" style={{ animationDelay: '1.1s' }} />
        <div className="absolute bottom-16 right-[20%] w-2 h-2 rounded-full bg-cyan-400/25 float-shape" style={{ animationDelay: '1.8s' }} />
        {/* Cross / sparkle */}
        <svg className="absolute top-40 right-[5%] opacity-15 float-shape" style={{ animationDelay: '0.6s' }} width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="1.5"><path d="M12 2v20M2 12h20"/></svg>
        <svg className="absolute bottom-20 left-[6%] opacity-10 float-shape" style={{ animationDelay: '2s' }} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#8B5CF6" strokeWidth="1.5"><path d="M12 2v20M2 12h20"/></svg>
      </div>

      {/* Content */}
      <div className={`relative z-10 ${align === 'left' ? 'max-w-7xl' : 'max-w-[900px]'} mx-auto px-4 sm:px-6 lg:px-8 flex flex-col ${itemsAlign} gap-4`}>
        {label && (
          <span className="section-label">{label}</span>
        )}
        <h1 className={`text-3xl sm:text-5xl md:text-6xl font-extrabold tracking-tight leading-[1.1] ${textAlign}`}
          style={{ color: 'var(--pd-text)' }}>
          {title}
        </h1>
        {subtitle && (
          <p className={`text-base sm:text-lg max-w-2xl ${textAlign}`}
            style={{ color: 'var(--pd-text2)' }}>
            {subtitle}
          </p>
        )}
        {children && (
          <div className={`flex flex-wrap gap-3 mt-2 ${align === 'center' ? 'justify-center' : ''}`}>
            {children}
          </div>
        )}
      </div>
    </div>
  );
};

export default PageHero;
